import { watch } from '@vue/composition-api';

import { UIEventBus, ROOT_EVENTS } from '@/common/ui-event-bus';
import { useHelpers } from './use-helpers';
import { useLayoutSwitcher } from '@/apps/agent-ui/modules/layout-switcher';
import { useSessions } from '@/modules/sessions-store';
import { useContacts } from '@/modules/contacts-store';
import { useWSEventsWorker } from '@/workers/ws-events';

export const useExternalEvents = () => {
  const {
    activeSession,
    setActiveSession,
    startSessionByTriggerId,
    claimSessionByAgent,
    applyCommandToSession,
    applyMessageToSession,
    applyRejectToSession,
    closeSessionById,
  } = useSessions();

  const { changeWorkspaceOrLayout } = useLayoutSwitcher();
  const { activeContact, setActiveContactById } = useContacts();
  const { getRegisterComponentApi } = useHelpers();
  const { sendWSMessage } = useWSEventsWorker();

  const wrapInitializeByTriggerId = async (data: any) => {
    const session = await startSessionByTriggerId(data);
    UIEventBus.root.$emit(ROOT_EVENTS.START_CONVERSATION_SUCCESS, session);
  };

  const onCardReadyEvent = (event: { cardId: string; callbackId: string;}) => {
    const { cardId, callbackId } = event;
    UIEventBus.root.$emit(ROOT_EVENTS.CURRENT_STATE, {
      callbackId,
      cardId,
      selectedContact: activeContact.value,
      selectedSession: activeSession.value,
    });
  };

  const onGetActiveContact = (args: { cardId: string; callbackId: string; }) => {
    const { cardId, callbackId } = args;
    UIEventBus.root.$emit(ROOT_EVENTS.SELECTED_CONTACT, {
      callbackId,
      cardId,
      contact: activeContact.value,
    });
  };

  const onGetActiveSession = (args: { cardId: string; callbackId: string; }) => {
    const { cardId, callbackId } = args;
    UIEventBus.root.$emit(ROOT_EVENTS.SELECTED_SESSION, {
      callbackId,
      cardId,
      session: activeSession.value,
    });
  };

  const onEvent = async (
    callback: (params?: any) => any | Promise<any>,
    event: { cardId: string; callbackId: string; params: any; },
    successEvent?: string
  ) => {
    const { cardId, callbackId, params } = event;
    const result = await callback(params);
    if ((cardId || callbackId) && successEvent) {
      UIEventBus.root.$emit(successEvent, { cardId, callbackId, result });
    }
  };

  /** Subscribe all event listeners only once */
  const onAppReady = () => {
    const EVENTS_MAP: Record<string, (...args: any) => void> = {
      [ROOT_EVENTS.START_CONVERSATION]: wrapInitializeByTriggerId,
      [ROOT_EVENTS.CARD_READY]: onCardReadyEvent,
      [ROOT_EVENTS.GET_SELECTED_CONTACT]: onGetActiveContact,
      [ROOT_EVENTS.GET_SELECTED_SESSION]: onGetActiveSession,
      [ROOT_EVENTS.SELECT_SESSION]: args => onEvent(setActiveSession, args, ROOT_EVENTS.SELECT_SESSION_SUCCESS),
      [ROOT_EVENTS.CLAIM_SESSION]: args => onEvent(claimSessionByAgent, args, ROOT_EVENTS.CLAIM_SESSION_SUCCESS),
      [ROOT_EVENTS.REJECT_CLAIM]: args => onEvent(applyRejectToSession, args, ROOT_EVENTS.REJECT_CLAIM_SUCCESS),
      [ROOT_EVENTS.CLOSE_SESSION]: args => onEvent(closeSessionById, args, ROOT_EVENTS.CLOSE_SESSION_SUCCESS),
      [ROOT_EVENTS.SELECT_CONTACT]: args => onEvent(setActiveContactById, args, ROOT_EVENTS.SELECT_CONTACT_SUCCESS),
      [ROOT_EVENTS.SEND_MESSAGE]: args => onEvent(applyMessageToSession, args, ROOT_EVENTS.SEND_MESSAGE_SUCCESS),
      [ROOT_EVENTS.SEND_COMMAND]: args => onEvent(applyCommandToSession, args, ROOT_EVENTS.SEND_COMMAND_SUCCESS),
      [ROOT_EVENTS.SEND_WS_MESSAGE]: args => onEvent(sendWSMessage, args, ROOT_EVENTS.SEND_WS_MESSAGE_SUCCESS),
      [ROOT_EVENTS.CHANGE_WORKSPACE]: args => onEvent(changeWorkspaceOrLayout, args),
    };

    Object.entries(EVENTS_MAP).map(([event, callback]) => UIEventBus.root.$on(event, callback));
    UIEventBus.root.$off(ROOT_EVENTS.APP_READY, onAppReady);
  };

  const subscribeToExternalEvents = () => {
    /* Card message bus is available to all cards as a global API */
    const common = getRegisterComponentApi().getCommonArgs();
    UIEventBus.setActionDeskViewEventBus(common.viewMessageBus);
    UIEventBus.setRootEventBus(common.cardMessageBus);
    UIEventBus.root.$on(ROOT_EVENTS.APP_READY, onAppReady);
  };

  watch(activeContact, () => {
    UIEventBus.root.$emit(ROOT_EVENTS.SELECTED_CONTACT_UPDATE, activeContact.value);
  });

  watch(activeSession, () => {
    UIEventBus.root.$emit(ROOT_EVENTS.SELECTED_SESSION_UPDATE, activeSession.value);
  });

  return { subscribeToExternalEvents };
};
